import React from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';

const FREDOKA = { fontFamily: "'Fredoka', sans-serif" };

const ConfirmationModal = ({
  isOpen,
  onConfirm,
  onCancel,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmColor = 'blue',
  starCost = null,
  currentStars = null,
  actionType = null,
  isConfirmLoading = false,
  confirmDisabled = false,
}) => {
  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (isConfirmLoading) return;
    if (e.target === e.currentTarget) onCancel();
  };

  const hasStarInfo = starCost !== null && currentStars !== null && actionType;
  const confirmBlocked = isConfirmLoading || confirmDisabled;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleBackdropClick}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(11,26,51,0.42)", backdropFilter: "blur(8px)", fontFamily: "'Inter', 'Fredoka', sans-serif" }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md overflow-hidden p-7 text-center"
            style={{
              background: "linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)",
              border: "2px solid rgba(11,26,51,0.06)",
              borderRadius: 28,
              boxShadow: "0px 5px 0px rgba(11,26,51,0.10), 0px 24px 64px rgba(11,26,51,0.20)",
            }}
          >
            {/* Decorative blob */}
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                top: -80,
                right: -80,
                width: 220,
                height: 220,
                borderRadius: "50%",
                background: "rgba(0,101,248,0.10)",
                filter: "blur(20px)",
                pointerEvents: "none",
              }}
            />

            <div className="relative">
              {/* Star medallion */}
              <motion.div
                initial={{ scale: 0, rotate: -20 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.05, type: 'spring', damping: 14, stiffness: 320 }}
                className="mb-5 mx-auto flex items-center justify-center"
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: 20,
                  background: "linear-gradient(135deg, #0065F8 0%, #00CAFF 100%)",
                  boxShadow: "0px 4px 0px #073B8E, 0px 12px 28px rgba(0,101,248,0.30)",
                }}
              >
                <img
                  src="/assets/pixley_stars.png"
                  alt=""
                  style={{ width: 38, height: 38, filter: "brightness(0) invert(1)" }}
                />
              </motion.div>

              {/* Title */}
              <motion.h2
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                style={{
                  ...FREDOKA,
                  color: "#0B1A33",
                  fontWeight: 700,
                  fontSize: "clamp(1.4rem, 3vw, 1.9rem)",
                  textTransform: "uppercase",
                  letterSpacing: "-0.01em",
                  lineHeight: 1.1,
                  margin: "0 0 18px",
                }}
              >
                {title}
              </motion.h2>

              {/* Cost & balance */}
              {hasStarInfo ? (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 }}
                  className="w-full mb-5"
                >
                  <div
                    className="flex items-center justify-between mb-2.5"
                    style={{
                      background: "#FFFFFF",
                      border: "2px solid rgba(0,101,248,0.18)",
                      borderRadius: 16,
                      padding: "14px 18px",
                      boxShadow: "0px 2px 0px rgba(11,26,51,0.04)",
                    }}
                  >
                    <span style={{ ...FREDOKA, color: "#5A6B85", fontSize: 11, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase" }}>
                      Creation Cost
                    </span>
                    <span className="inline-flex items-center gap-1.5" style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 800, fontSize: 22, lineHeight: 1 }}>
                      {starCost}
                      <img src="/assets/pixley_stars.png" alt="" style={{ width: 18, height: 18 }} />
                    </span>
                  </div>

                  <div
                    className="overflow-hidden"
                    style={{
                      background: "#FFFFFF",
                      border: "2px solid rgba(11,26,51,0.08)",
                      borderRadius: 16,
                      boxShadow: "0px 2px 0px rgba(11,26,51,0.04)",
                    }}
                  >
                    <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: "1.5px solid rgba(11,26,51,0.06)" }}>
                      <span style={{ ...FREDOKA, color: "#94A3B8", fontSize: 11, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase" }}>
                        Current Balance
                      </span>
                      <span className="inline-flex items-center gap-1.5" style={{ ...FREDOKA, color: "#5A6B85", fontWeight: 700, fontSize: 15 }}>
                        {currentStars.toLocaleString()}
                        <img src="/assets/pixley_stars.png" alt="" style={{ width: 14, height: 14 }} />
                      </span>
                    </div>
                    <div className="flex items-center justify-between px-4 py-3">
                      <span style={{ ...FREDOKA, color: "#0065F8", fontSize: 11, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase" }}>
                        After
                      </span>
                      <span className="inline-flex items-center gap-1.5" style={{ ...FREDOKA, color: "#0065F8", fontWeight: 800, fontSize: 17 }}>
                        {(currentStars - starCost).toLocaleString()}
                        <img src="/assets/pixley_stars.png" alt="" style={{ width: 14, height: 14 }} />
                      </span>
                    </div>
                  </div>
                </motion.div>
              ) : (
                <motion.p
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 }}
                  style={{ ...FREDOKA, color: "#5A6B85", fontSize: 14, margin: "0 0 22px" }}
                >
                  {message}
                </motion.p>
              )}

              {/* Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="flex flex-col gap-2.5"
              >
                <button
                  onClick={confirmBlocked ? undefined : onConfirm}
                  disabled={confirmBlocked}
                  aria-busy={isConfirmLoading}
                  className="w-full transition active:scale-[0.97] inline-flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed disabled:active:scale-100"
                  style={{
                    ...FREDOKA,
                    background: "#0065F8",
                    color: "#FFFFFF",
                    fontWeight: 700,
                    fontSize: 15,
                    border: "none",
                    borderRadius: 999,
                    padding: "13px 22px",
                    boxShadow: "0px 4px 0px #073B8E, 0px 12px 24px rgba(0,101,248,0.22)",
                    cursor: confirmBlocked ? "not-allowed" : "pointer",
                  }}
                >
                  {isConfirmLoading && (
                    <span
                      aria-hidden="true"
                      style={{
                        width: 16,
                        height: 16,
                        borderRadius: "50%",
                        border: "2px solid rgba(255,255,255,0.35)",
                        borderTopColor: "#FFFFFF",
                        animation: "confirmationModalSpin 0.7s linear infinite",
                        flexShrink: 0,
                      }}
                    />
                  )}
                  {confirmText}
                  {hasStarInfo && !isConfirmLoading && (
                    <span
                      className="inline-flex items-center gap-1"
                      style={{
                        background: "rgba(255,255,255,0.22)",
                        borderRadius: 999,
                        padding: "2px 8px",
                        fontSize: 12,
                        fontWeight: 800,
                      }}
                    >
                      {starCost}
                      <img src="/assets/pixley_stars.png" alt="" style={{ width: 12, height: 12, filter: "brightness(0) invert(1)" }} />
                    </span>
                  )}
                </button>
                <button
                  onClick={isConfirmLoading ? undefined : onCancel}
                  disabled={isConfirmLoading}
                  className="w-full transition active:scale-[0.97] disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100"
                  style={{
                    ...FREDOKA,
                    background: "#FFFFFF",
                    color: "#5A6B85",
                    fontWeight: 700,
                    fontSize: 14,
                    border: "2px solid rgba(11,26,51,0.10)",
                    borderRadius: 999,
                    padding: "11px 22px",
                    boxShadow: "0px 2px 0px rgba(11,26,51,0.05)",
                    cursor: isConfirmLoading ? "not-allowed" : "pointer",
                  }}
                >
                  {cancelText}
                </button>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      )}
      <style>{`
        @keyframes confirmationModalSpin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </AnimatePresence>,
    document.body
  );
};

export default ConfirmationModal;
